import { z } from "zod";

import type { DbEntity } from "../../../client";
import { DbEnum } from "../../../generated/prisma-enums";
import type { PaginatedResponse } from "../../../shared/core/base.types";
import { workflowDetailsSchemaBase } from "../../../shared/domain/workflow/workflow-details.types";
import { ApiSuccessSchema } from "../../common/response.schema";
import {
  TriggerWebhookSecretPathVarsSchema,
  TriggerWebhookSecretSchema
} from "./trigger-webhook-secret";

/**
 * Version-history shapes. Modelled with `z.custom` passthroughs (not the
 * `@nexus/types` `WorkflowVersionSchema`, whose `z.date()` + `.uuid()` fields
 * would reject the serialized payload on a strict `.parse()`). The types mirror
 * the backend service return — `createdAt` is typed `Date` to match what the
 * controller wraps, though the JSON wire delivers an ISO string; consumers read
 * it through `new Date(...)`, which tolerates both. Runtime validation stays a
 * no-op, exactly as the legacy type-only hooks behaved.
 */
export type WorkflowVersionMetadata = {
  id: string;
  versionNumber: number;
  description: string | null;
  isPublished: boolean;
  createdAt: Date;
};

export type WorkflowVersionDetail = WorkflowVersionMetadata & {
  workflowId: string;
  nodes: unknown[];
  edges: unknown[];
};

/**
 * Execution list-row shapes. Both mirror the backend Prisma `select`
 * projections (workflow-execution-history.service.ts) plus the cost/node-count
 * fields attached after the recursive LLMGeneration rollup. Modelled as
 * `z.custom` passthroughs — timestamps are typed `Date` to match the service
 * return, though the JSON wire delivers ISO strings; consumers read them
 * through `new Date(...)`, which tolerates both.
 */
export type WorkflowExecutionSummary = {
  id: string;
  status: DbEntity.WorkflowExecution["status"];
  createdAt: Date;
  executionStartedAt: Date | null;
  executionStoppedAt: Date | null;
  wasTestExecution: boolean;
  totalCostInUSDTenThousandths: number;
  totalNodesExecuted: number;
};

/** One row of the org-wide executions list, with its parent-workflow header. */
export type WorkflowExecutionListItem = {
  id: string;
  createdAt: Date;
  status: DbEntity.WorkflowExecution["status"];
  parentNodeId: string | null;
  organizationId: string;
  workflowId: string;
  executionStartedAt: Date | null;
  executionStoppedAt: Date | null;
  totalCostInUSDTenThousandths: number;
  totalNodesExecuted: number;
  workflow: {
    id: string;
    name: string;
    iconUrl: string | null;
  };
};

/**
 * One row of the conversation.idle Test panel chat picker, hydrated by the
 * backend so the picker UI and the post-fire success card render against the
 * same shape. Mirrors the scanner's `TestPickerCandidate` projection exactly —
 * all timestamps arrive as ISO strings (already serialized server-side), so
 * plain `z.string()` fields validate the wire without the `z.date()`
 * constraint-activation trap.
 *
 * Customer fields are independently nullable (a Google Sheets session has no
 * identity at all; a fresh embed session may have an identifier but no
 * displayName yet) — renderers fall through displayName → identifier →
 * primaryPhone → primaryEmail and never assume one is present.
 */
export const IdleTestPickerCandidateSchema = z.object({
  id: z.string(),
  lastMessageUpdatedAt: z.string().nullable(),
  deployment: z.object({
    id: z.string().nullable(),
    type: z.enum(DbEnum.DEPLOYMENT_TYPE_VALUES).nullable(),
    name: z.string().nullable()
  }),
  customer: z.object({
    displayName: z.string().nullable(),
    primaryEmail: z.string().nullable(),
    primaryPhone: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    identifier: z.string().nullable()
  }),
  lastMessage: z.object({ content: z.string().nullable(), createdAt: z.string() }).nullable(),
  hasFiredBefore: z.boolean()
});

export type IdleTestPickerCandidate = z.infer<typeof IdleTestPickerCandidateSchema>;

/** Trust-UX diagnostics returned alongside the idle-match preview count. */
export const IdleMatchCountDiagnosticsSchema = z.object({
  candidatesBeforeFilter: z.number(),
  candidatesAfterFilter: z.number(),
  capHit: z.boolean()
});

export type IdleMatchCountDiagnostics = z.infer<typeof IdleMatchCountDiagnosticsSchema>;

/**
 * One row of the workflow list reads — the exact Prisma `select` / raw-SQL
 * projection in workflow-query.service.ts (both permission paths select the
 * same columns). `name` and the enum fields follow the Prisma schema (the
 * service's local row interface types them looser than the DB allows).
 * Modelled as a `z.custom` passthrough: timestamps are typed `Date` to match
 * the service return; the wire delivers ISO strings.
 */
export type WorkflowListItem = {
  id: string;
  name: string;
  iconUrl: string | null;
  status: DbEntity.Workflow["status"];
  createdAt: Date;
  updatedAt: Date | null;
  agentInputSchema: unknown;
  description: string | null;
  triggerType: DbEntity.Workflow["triggerType"];
};

/** Agent-input-triggered rows carry the same projection minus description/triggerType. */
export type AgentInputTriggerWorkflowListItem = Pick<
  WorkflowListItem,
  "id" | "name" | "iconUrl" | "status" | "createdAt" | "updatedAt" | "agentInputSchema"
>;

/**
 * Executor payload carried under a node test result's `data`. Workflow nodes
 * expose the sub-workflow identifiers here; other node types carry arbitrary
 * executor output, so unknown keys stay open via the index signature.
 */
export type TestWorkflowNodeData = {
  subWorkflowExecutionId?: string;
  subWorkflowId?: string;
  shouldPause?: boolean;
  [key: string]: unknown;
};

/**
 * `POST /api/workflow/:id/testNode` — the async arm.
 *
 * Long-running node types (plugin, firecrawl, exaai, sixtyfour, aiTask, cueNode,
 * loop, and non-search/chat parallelai) are dispatched in the background and the
 * reply carries no result. `jobId` and `testExecutionId` are the SAME
 * `WorkflowExecutionNode` primary key, assigned from one expression — poll
 * `GET /api/workflow/job/:jobId` for the outcome.
 */
export type WorkflowNodeTestPending = {
  /** Always `null`: the execution has not run when this reply is written. */
  data: null;
  jobId: string;
  testExecutionId: string;
  status: Extract<DbEntity.WorkflowExecutionNode["status"], "PENDING">;
  nodeType: string;
  /** Absent on this arm — the run has not settled, so it has not failed either. */
  failed?: never;
  /**
   * Absent on this arm for the same reason `data` is `null`: the node has not
   * run, so it has reported nothing about the run. Poll the job, then read the
   * result route.
   */
  metadata?: never;
};

/**
 * `POST /api/workflow/:id/testNode` — the synchronous arm, run to completion.
 *
 * `data` is whatever the node executor returned, and `AbstractNode.execute`
 * declares no result type at all — so it can be an object, a string, or `null`.
 * Never narrow a consumer to assume the object case.
 */
export type WorkflowNodeTestSettled = {
  data: unknown;
  /**
   * What the node reported ABOUT the run, beside — never inside — `data`.
   *
   * A `smartAction` node names the candidate it chose here; before NEX-4065 it
   * merged those keys INTO the tool's own result object, which both polluted
   * every tool result and overwrote a tool's own `chosenTool` field. The same
   * object is persisted to `WorkflowExecutionNode.metadata` and read back from
   * {@link WorkflowNodeTestResult}.
   *
   * Absent for the node types that have nothing to say about a run, which is
   * most of them — never narrow a consumer to assume it is there.
   */
  metadata?: Record<string, unknown>;
  testExecutionId: string;
  /**
   * REQUIRED, and required rather than optional on purpose. The failed arm used
   * to omit it entirely, and its one API consumer read `result.status ??
   * "COMPLETED"` — so a node that had just been persisted as SKIPPED was
   * reported to the caller as COMPLETED, carrying the failure only inside
   * `data.error` (NEX-4045). A required field makes the omission a compile
   * error at the writer instead of a wrong status at the reader.
   *
   * It mirrors the row this call has already written: this arm is the success
   * return, so it is always `COMPLETED` — so a later `GET` of the same
   * execution agrees. The failure return carries its own `SKIPPED` on
   * {@link WorkflowNodeTestFailed}.
   */
  status: Extract<DbEntity.WorkflowExecutionNode["status"], "COMPLETED">;
  /** Absent on this arm — present only while the async job is pending. */
  jobId?: never;
  nodeType?: never;
  /**
   * The discriminant, `false` or absent here. See
   * {@link WorkflowNodeTestFailed} for why it exists.
   */
  failed?: false;
};

/**
 * The error envelope a failed synchronous node test replies with — the same
 * object the run's `WorkflowExecutionNode.data` stores, so a poller reading the
 * row back sees exactly these keys.
 */
export type WorkflowNodeTestErrorEnvelope = {
  /** `errorDetails.message`, hoisted for readers that only want the string. */
  error: string;
  errorDetails: Record<string, unknown>;
  /** ISO-8601, stamped when the failure was caught. */
  timestamp: string;
};

/**
 * `POST /api/workflow/:id/testNode` — the synchronous arm when the executor
 * threw. The service catches, persists the node as `SKIPPED` and the parent
 * execution as `FAILED`, then *returns* the error envelope rather than throwing.
 *
 * `failed` exists because the envelope alone cannot be told apart from a real
 * executor output that happens to carry `error`/`errorDetails`/`timestamp` keys.
 * Without a discriminant every consumer had to guess, and the Public API's
 * write-back guessed "success": it inferred `outputFormat` from the FAILURE and
 * published it as the node's contract to downstream nodes (NEX-4066). Read this
 * flag — never the shape of `data` — to decide whether a run succeeded.
 */
export type WorkflowNodeTestFailed = {
  /** Always `true` — the discriminant. */
  failed: true;
  data: WorkflowNodeTestErrorEnvelope;
  testExecutionId: string;
  /**
   * The state this call has already persisted on the node's row: always
   * `SKIPPED`. Required for the same reason as on
   * {@link WorkflowNodeTestSettled} — omitting it let the one API consumer
   * default to `"COMPLETED"` over a node it had just stored as SKIPPED on an
   * execution finalized as FAILED (NEX-4045). The Public API v1 reports this
   * arm's OUTCOME as `FAILED` (see NEX-4066); this field is the row's state,
   * so a later `GET` of the node agrees with it.
   */
  status: Extract<DbEntity.WorkflowExecutionNode["status"], "SKIPPED">;
  /** Absent on this arm — present only while the async job is pending. */
  jobId?: never;
  nodeType?: never;
  /**
   * Absent on this arm: run metadata (NEX-4065) is attached only on the
   * success path, so a failed run has reported nothing beside its envelope.
   */
  metadata?: never;
};

/**
 * The complete `POST /api/workflow/:id/testNode` contract:
 * `WorkflowNodeTestingService.testNode` has exactly three returns and they
 * collapse to these three shapes. Annotated on the service so a fourth shape is
 * a compile error at the writer instead of a silent `undefined` at the reader.
 *
 * `testExecutionId` and `status` are required on EVERY arm — the first is a
 * `prisma.workflowExecutionNode.create()` primary key and is never absent, and
 * the second is the state that call has just written, so no consumer has to
 * assume one (NEX-4045). Every other top-level field belongs to one arm alone;
 * a hand-written mirror of this response once declared `loopExecutions`, `id` and
 * `subWorkflowExecutionId` at the top level, none of which any return produces.
 * `loopExecutions` belongs to {@link WorkflowNodeTestResult} (the GET route);
 * `subWorkflowExecutionId` lives inside `data` for workflow nodes.
 *
 * Narrow on `failed` first, `jobId` second: the failure arm is a settled run
 * whose output must NOT be treated as the node's output.
 */
export type WorkflowNodeTestResponse =
  | WorkflowNodeTestPending
  | WorkflowNodeTestSettled
  | WorkflowNodeTestFailed;

/**
 * GET /api/workflow/test-result/:testExecutionId response. Mirrors the
 * response object built in workflow-execution-history.service.ts —
 * `loopExecutions`/`metadata` are only present for loop-node tests. Modelled
 * as a `z.custom` passthrough: timestamps are typed `Date` to match the
 * service return (the wire delivers ISO strings; consumers read them through
 * `new Date(...)`).
 *
 * A strict subset of the row: the service selects neither `nodeType` nor
 * `updatedAt`, `errorMessage`, `isRetryable` or `label`. Reading any of those
 * off this result yields `undefined` at runtime, which is why it is not — and
 * must not be cast to — {@link WorkflowExecutionLightNode}.
 */
export type WorkflowNodeTestResult = {
  id: string;
  data: TestWorkflowNodeData;
  status: DbEntity.WorkflowExecutionNode["status"];
  createdAt: Date;
  finishedAt: Date | null;
  nodeId: string;
  /**
   * The per-iteration executions of a loop node, restored from the
   * `executionsWaitingFor` relation. The service serves whole rows; only the
   * fields below are read, so this is deliberately narrower than what arrives.
   * Present only when the node is a loop test that has created iterations.
   */
  loopExecutions?: Array<
    Pick<DbEntity.WorkflowExecution, "id" | "status" | "flowFinished" | "wasOutputRun">
  >;
  metadata?: Record<string, unknown>;
};

/**
 * GET /api/workflow/job/:jobId response — background node-job polling
 * projection built in workflow-node-testing.service.ts. `data` is a Prisma
 * Json column with no contract; consumers narrow it themselves.
 */
export type WorkflowNodeJobStatus = {
  jobId: string;
  nodeId: string;
  status: DbEntity.WorkflowExecutionNode["status"];
  data: unknown;
  createdAt: Date;
  finishedAt: Date | null;
};

/**
 * One execution node as GET /api/workflow/:executionId/workflow-execution-light
 * serves it — a strict subset of the row.
 *
 * The route is polled every 2s, so it deliberately omits the heavy columns
 * `data` and `input` — lazy-loaded per node by the node-detail route when a node
 * is opened — and simply does not select `workflowExecutionId`,
 * `organizationId`, `parentId`, `retryCount` or `metaConversationId`. Reading
 * any of those off a node from this route yields `undefined` at runtime, which
 * is why this is a `Pick` and not the full `DbEntity.WorkflowExecutionNode`.
 * Typing it as the full row is what let a `node.data.chosenBranch` read survive
 * seven months (NEX-2752).
 *
 * Derived from `DbEntity` rather than hand-written so nullability and schema
 * changes track Prisma. `getWorkflowExecutionLight` annotates its return with
 * {@link WorkflowExecutionLightResponse}, so narrowing the select below this
 * shape is a compile error rather than a silent `undefined`.
 */
export type WorkflowExecutionLightNode = Pick<
  DbEntity.WorkflowExecutionNode,
  | "id"
  | "status"
  | "createdAt"
  | "updatedAt"
  | "finishedAt"
  | "nodeId"
  | "metadata"
  | "errorMessage"
  | "isRetryable"
  | "label"
  | "nodeType"
> & {
  executionsWaitingFor: Array<
    Pick<DbEntity.WorkflowExecution, "id" | "status" | "flowFinished" | "wasOutputRun">
  >;
  /**
   * Branching nodes only — the branch this node took, projected out of the
   * omitted `data` blob by the service. Absent while a node is unresolved.
   */
  chosenBranch?: string;
};

/**
 * GET /api/workflow/:executionId/workflow-execution-light response.
 *
 * Carries no graph — consumers render against the current workflow's nodes.
 * `workflow` is selected purely to authorize the request, so `organizationId`
 * is the only field on it.
 */
export type WorkflowExecutionLightResponse = Pick<
  DbEntity.WorkflowExecution,
  "id" | "status" | "createdAt" | "workflowId" | "wasTestExecution"
> & {
  executionHistory: WorkflowExecutionLightNode[];
  workflow: { organizationId: string };
  totalNodesExecuted: number;
  totalCostInUSDTenThousandths: number;
};

/**
 * A PARTIAL rename/redescribe, DERIVED from {@link workflowDetailsSchemaBase}
 * rather than restated — see that schema's own note for why a second copy of
 * these two field rules is a drift waiting to happen.
 *
 * `.pick()` is not redundant on a two-field base: it PINS which fields this
 * JSON door accepts. Growing the base — an `iconUrl`, say — would otherwise
 * silently widen this body to a field the handler never writes.
 *
 * 🚨 AT LEAST ONE FIELD IS REQUIRED, and that refusal is the schema's job
 * rather than a nicety. `.partial()` alone accepts `{}`, which would bump
 * `updatedAt` and emit both a structure-changed socket event and an Echo audit
 * row for a request that changed nothing — a no-op that reads as a real edit in
 * every audit surface downstream.
 */
const WorkflowDetailsPartialBody = workflowDetailsSchemaBase
  .pick({ name: true, description: true })
  .partial()
  .refine(
    (body) => body.name !== undefined || body.description !== undefined,
    "Provide at least one of name or description"
  );

export const ZWorkflows = {
  Get: {
    method: "GET" as const,
    path: "/api/workflow/:id",
    PathVars: z.object({ id: z.string() }),
    Response: ApiSuccessSchema(z.custom<DbEntity.Workflow>())
  },

  /**
   * GET /api/workflow — non-archived workflows for the org, newest-updated
   * first, filtered by the caller's permission condition server-side.
   */
  List: {
    method: "GET" as const,
    path: "/api/workflow",
    Response: ApiSuccessSchema(z.custom<WorkflowListItem[]>())
  },

  /** GET /api/workflow/agent-input-triggered — workflows with an AGENT trigger. */
  ListAgentInputTriggered: {
    method: "GET" as const,
    path: "/api/workflow/agent-input-triggered",
    Response: ApiSuccessSchema(z.custom<AgentInputTriggerWorkflowListItem[]>())
  },

  /**
   * GET /api/workflow/archived/list — archived workflows for the My Tools
   * "Archived" sub-filter (the merged skills feed intentionally excludes them).
   */
  ListArchived: {
    method: "GET" as const,
    path: "/api/workflow/archived/list",
    Response: ApiSuccessSchema(z.custom<WorkflowListItem[]>())
  },

  /**
   * PATCH /api/workflow/:id/details — rename a workflow, or rewrite its
   * description, without sending anything else.
   *
   * 🚨 `PUT /api/workflow/:id/details` STAYS, AND IS NOT LEGACY. It is
   * `multipart/form-data` because it also carries the icon FILE, and `name` is
   * required on it. Both properties are right for the canvas header's Edit
   * dialog, which uploads an icon; both are wrong for a menu's Rename row,
   * which has no file part and must be able to send a description on its own.
   * So the two coexist on purpose: multipart when there is a file, JSON when
   * there is not. Do not fold either into the other.
   *
   * PATCH rather than PUT: this writes only the keys it was sent, which is
   * what PATCH means, and it is what distinguishes the two routes at the
   * method level so both can share the path.
   *
   * The handler writes each field conditionally, so a rename cannot clear a
   * description and a description edit cannot clear a name — the read-modify-
   * write that the multipart route forces on a caller who only wanted to
   * rename is exactly what this route exists to remove.
   */
  UpdateDetails: {
    method: "PATCH" as const,
    path: "/api/workflow/:id/details",
    PathVars: z.object({ id: z.string() }),
    Body: WorkflowDetailsPartialBody,
    Response: ApiSuccessSchema(z.custom<DbEntity.Workflow>())
  },

  /**
   * DELETE /api/workflow/:id — soft-archive a workflow (status → ARCHIVED,
   * referencing agent skills deactivated). Returns the archived row.
   */
  Archive: {
    method: "DELETE" as const,
    path: "/api/workflow/:id",
    PathVars: z.object({ id: z.string() }),
    Response: ApiSuccessSchema(z.custom<DbEntity.Workflow>())
  },

  /** POST /api/workflow/:id/restore — restore an archived workflow to DRAFT. */
  Restore: {
    method: "POST" as const,
    path: "/api/workflow/:id/restore",
    PathVars: z.object({ id: z.string() }),
    Response: ApiSuccessSchema(z.custom<DbEntity.Workflow>())
  },

  /**
   * POST /api/workflow/:id/publish — publish the draft (sanitized
   * nodes/edges copied to publishedNodes/publishedEdges). Returns only the
   * fresh `updatedAt` (the service selects it to feed the optimistic-lock
   * check on the next draft save), typed Date to match the service return;
   * the wire delivers an ISO string.
   */
  Publish: {
    method: "POST" as const,
    path: "/api/workflow/:id/publish",
    PathVars: z.object({ id: z.string() }),
    Response: ApiSuccessSchema(z.custom<{ updatedAt: Date | null }>())
  },

  /** POST /api/workflow/:id/unpublish — drop back to draft-only; returns a status string. */
  Unpublish: {
    method: "POST" as const,
    path: "/api/workflow/:id/unpublish",
    PathVars: z.object({ id: z.string() }),
    Response: ApiSuccessSchema(z.string())
  },

  /** POST /api/workflow/:id/duplicate — copy a workflow; returns the new row. */
  Duplicate: {
    method: "POST" as const,
    path: "/api/workflow/:id/duplicate",
    PathVars: z.object({ id: z.string() }),
    Response: ApiSuccessSchema(z.custom<DbEntity.Workflow>())
  },

  /** DELETE /api/workflow/execution/:executionId — cancel a running/pending execution. */
  CancelExecution: {
    method: "DELETE" as const,
    path: "/api/workflow/execution/:executionId",
    PathVars: z.object({ executionId: z.string() }),
    Response: ApiSuccessSchema(
      z.object({
        success: z.boolean(),
        message: z.string(),
        /** Root plus every descendant (loop iteration) execution that was cancelled. */
        cancelledExecutions: z.number().int().nonnegative()
      })
    )
  },

  /** DELETE /api/workflow/execution/:executionId/delete — permanently delete an execution. */
  DeleteExecution: {
    method: "DELETE" as const,
    path: "/api/workflow/execution/:executionId/delete",
    PathVars: z.object({ executionId: z.string() }),
    Response: ApiSuccessSchema(z.object({ success: z.boolean(), message: z.string() }))
  },

  /** GET /api/workflow/:executionId/export — export an execution as JSON, returns a signed S3 URL. */
  ExportExecution: {
    method: "GET" as const,
    path: "/api/workflow/:executionId/export",
    PathVars: z.object({ executionId: z.string() }),
    Response: ApiSuccessSchema(z.object({ downloadUrl: z.string() }))
  },

  /**
   * GET /api/workflow/:id/workflow-executions — top-level (non-test) execution
   * summaries for one workflow, newest first, with rolled-up cost/node counts.
   */
  GetExecutions: {
    method: "GET" as const,
    path: "/api/workflow/:id/workflow-executions",
    PathVars: z.object({ id: z.string() }),
    Response: ApiSuccessSchema(z.custom<WorkflowExecutionSummary[]>())
  },

  /**
   * GET /api/workflow/executions/all — org-wide paginated top-level executions.
   * `status` is a comma-separated WorkflowExecutionStatus list; date bounds are
   * ISO strings. Mirrors the backend GetExecutionsQueryDto (page 1-indexed,
   * perPage capped at 100 server-side).
   */
  GetAllExecutions: {
    method: "GET" as const,
    path: "/api/workflow/executions/all",
    Query: z.object({
      page: z.number().optional(),
      perPage: z.number().optional(),
      workflowId: z.string().optional(),
      status: z.string().optional(),
      startDate: z.string().optional(),
      endDate: z.string().optional(),
      sortBy: z.enum(["createdAt", "status"]).optional(),
      order: z.enum(["asc", "desc"]).optional()
    }),
    Response: ApiSuccessSchema(z.custom<PaginatedResponse<WorkflowExecutionListItem>>())
  },

  /**
   * GET /api/workflow/test-result/:testExecutionId — node test result by test
   * execution id (the persisted WorkflowExecutionNode of a single-node test).
   */
  GetNodeTestResult: {
    method: "GET" as const,
    path: "/api/workflow/test-result/:testExecutionId",
    PathVars: z.object({ testExecutionId: z.string() }),
    Response: ApiSuccessSchema(z.custom<WorkflowNodeTestResult>())
  },

  /**
   * DELETE /api/workflow/test-execution/:testExecutionId — cancel a running
   * single-node test and any loop iterations it spawned.
   */
  CancelTestExecution: {
    method: "DELETE" as const,
    path: "/api/workflow/test-execution/:testExecutionId",
    PathVars: z.object({ testExecutionId: z.string() }),
    Response: ApiSuccessSchema(z.object({ success: z.boolean(), message: z.string() }))
  },

  /** GET /api/workflow/job/:jobId — poll a background node-test job's status. */
  GetNodeJobStatus: {
    method: "GET" as const,
    path: "/api/workflow/job/:jobId",
    PathVars: z.object({ jobId: z.string() }),
    Response: ApiSuccessSchema(z.custom<WorkflowNodeJobStatus>())
  },

  /**
   * POST /api/workflow/execution/:executionId/node/:nodeExecutionId/retry —
   * retry a failed (ERROR) node inside a still-running execution.
   */
  RetryNode: {
    method: "POST" as const,
    path: "/api/workflow/execution/:executionId/node/:nodeExecutionId/retry",
    PathVars: z.object({ executionId: z.string(), nodeExecutionId: z.string() }),
    Response: ApiSuccessSchema(z.object({ success: z.boolean(), message: z.string() }))
  },

  /**
   * POST /api/workflow/execution/:executionId/node/:nodeExecutionId/retry-iteration/:iterationIndex
   * — retry one iteration of a loop/do-while node (zero-based index).
   */
  RetryLoopIteration: {
    method: "POST" as const,
    path: "/api/workflow/execution/:executionId/node/:nodeExecutionId/retry-iteration/:iterationIndex",
    PathVars: z.object({
      executionId: z.string(),
      nodeExecutionId: z.string(),
      iterationIndex: z.string()
    }),
    Response: ApiSuccessSchema(z.object({ success: z.boolean(), message: z.string() }))
  },

  /** GET /api/workflow/:id/versions — paginated version metadata (no nodes/edges). */
  GetVersions: {
    method: "GET" as const,
    path: "/api/workflow/:id/versions",
    PathVars: z.object({ id: z.string() }),
    Query: z.object({ limit: z.number().optional(), offset: z.number().optional() }),
    Response: ApiSuccessSchema(z.custom<{ versions: WorkflowVersionMetadata[]; total: number }>())
  },

  /** GET /api/workflow/:id/versions/:versionId — a single version with full nodes/edges. */
  GetVersion: {
    method: "GET" as const,
    path: "/api/workflow/:id/versions/:versionId",
    PathVars: z.object({ id: z.string(), versionId: z.string() }),
    Response: ApiSuccessSchema(z.custom<WorkflowVersionDetail>())
  },

  /** POST /api/workflow/:id/versions/:versionId/rollback — restore the draft to a version. */
  RollbackToVersion: {
    method: "POST" as const,
    path: "/api/workflow/:id/versions/:versionId/rollback",
    PathVars: z.object({ id: z.string(), versionId: z.string() }),
    Response: ApiSuccessSchema(z.custom<DbEntity.Workflow>())
  },

  /** POST /api/workflow/:id/versions/:versionId/publish — publish a version as live. */
  PublishVersion: {
    method: "POST" as const,
    path: "/api/workflow/:id/versions/:versionId/publish",
    PathVars: z.object({ id: z.string(), versionId: z.string() }),
    Response: ApiSuccessSchema(z.custom<{ updatedAt: Date | null }>())
  },

  /** POST /api/workflow/:id/versions/:versionId/branch — fork a new workflow from a version. */
  BranchFromVersion: {
    method: "POST" as const,
    path: "/api/workflow/:id/versions/:versionId/branch",
    PathVars: z.object({ id: z.string(), versionId: z.string() }),
    Response: ApiSuccessSchema(z.custom<DbEntity.Workflow>())
  },

  /**
   * POST /api/workflow/:workflowId/triggers/preview-idle-match-count —
   * count-only would-fire preview against an in-progress (unpersisted) config.
   * No events, no ledger writes.
   */
  PreviewIdleMatchCount: {
    method: "POST" as const,
    path: "/api/workflow/:workflowId/triggers/preview-idle-match-count",
    PathVars: z.object({ workflowId: z.string() }),
    Body: z.object({
      triggerData: z.record(z.string(), z.unknown()),
      nodeId: z.string().optional()
    }),
    Response: ApiSuccessSchema(
      z.object({ count: z.number(), diagnostics: IdleMatchCountDiagnosticsSchema })
    )
  },

  /**
   * POST /api/workflow/:workflowId/triggers/test-fire-idle — single-chat fire
   * for the Test panel. `chatId` omitted → backend auto-picks the most recently
   * idle matching chat; `dryRun: true` → preview only (no ledger, no emit).
   * `firedAt` is null in dry-run, an ISO string on a real fire.
   */
  TestFireIdle: {
    method: "POST" as const,
    path: "/api/workflow/:workflowId/triggers/test-fire-idle",
    PathVars: z.object({ workflowId: z.string() }),
    Body: z.object({
      nodeId: z.string(),
      triggerData: z.record(z.string(), z.unknown()),
      chatId: z.string().optional(),
      dryRun: z.boolean().optional()
    }),
    Response: ApiSuccessSchema(
      z.object({
        chat: IdleTestPickerCandidateSchema,
        firedAt: z.string().nullable()
      })
    )
  },

  /**
   * POST /api/workflow/:workflowId/triggers/list-idle-test-candidates — source
   * list for the Test panel chat picker. Skips the idle threshold and re-arming
   * ledger (picker is a force-fire surface). `hasMore` is an accurate SQL-level
   * next-page upper bound.
   */
  /**
   * GET /api/workflow/:workflowId/triggers/:nodeId/webhook-secret — the secret a
   * sender presents on the production WEBHOOK trigger of that node, and whether
   * unauthenticated deliveries are refused. EDITOR on the workflow: anyone holding
   * the secret can start runs.
   */
  GetTriggerWebhookSecret: {
    method: "GET" as const,
    path: "/api/workflow/:workflowId/triggers/:nodeId/webhook-secret",
    PathVars: TriggerWebhookSecretPathVarsSchema,
    Response: ApiSuccessSchema(TriggerWebhookSecretSchema)
  },

  /**
   * POST /api/workflow/:workflowId/triggers/:nodeId/webhook-secret/rotate — issue a
   * new secret; the old one stops working at once. Enforcement is left as it was.
   */
  RotateTriggerWebhookSecret: {
    method: "POST" as const,
    path: "/api/workflow/:workflowId/triggers/:nodeId/webhook-secret/rotate",
    PathVars: TriggerWebhookSecretPathVarsSchema,
    Response: ApiSuccessSchema(TriggerWebhookSecretSchema)
  },

  /**
   * POST /api/workflow/:workflowId/triggers/:nodeId/webhook-secret/enforce — start
   * refusing deliveries that carry neither the secret nor a valid signature. For a
   * trigger published before secrets existed; one created since is already enforced.
   * Refused with 409 while the trigger has no secret to enforce.
   */
  EnforceTriggerWebhookSecret: {
    method: "POST" as const,
    path: "/api/workflow/:workflowId/triggers/:nodeId/webhook-secret/enforce",
    PathVars: TriggerWebhookSecretPathVarsSchema,
    Response: ApiSuccessSchema(TriggerWebhookSecretSchema)
  },

  ListIdleTestCandidates: {
    method: "POST" as const,
    path: "/api/workflow/:workflowId/triggers/list-idle-test-candidates",
    PathVars: z.object({ workflowId: z.string() }),
    Body: z.object({
      nodeId: z.string(),
      triggerData: z.record(z.string(), z.unknown()),
      query: z.string().optional(),
      limit: z.number().optional(),
      offset: z.number().optional()
    }),
    Response: ApiSuccessSchema(
      z.object({
        candidates: z.array(IdleTestPickerCandidateSchema),
        hasMore: z.boolean()
      })
    )
  }
} as const;
